import {
  Box,
  IconButton,
  Modal,
  Paper,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@material-ui/core';
import MoreIcon from '@material-ui/icons/MoreVert';
import { AxiosResponse, Canceler } from 'axios';
import moment from 'moment-timezone';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CopyToClipboard } from '../../../components/CopyToClipboard';
import { ActiveStatusLabel } from '../../../components/Curation/Labels';
import StoryList from '../../../components/StoryList';
import useBackdrop from '../../../hooks/useBackdrop';
import useConfirmDialog from '../../../hooks/useConfirmDialog';
import useSnackbar from '../../../hooks/useSnackbar';
import { getUserSegmentAbtest, getUserSegmentAbtests } from '../../../services/api/Abtest';
import api from '../../../services/api/base';
import { getCollection } from '../../../services/api/Collection';
import {
  IAbTestUpdateRequestDto,
  ICollection,
  IFeaturedCard,
  ISegmentCurationAbTest,
  ISegmentCurationCollection,
  ISegmentCurationFeaturedCard,
  ISegmentCurationStatus,
  ISegmentCurationType,
  IStory,
  IUserSegment,
} from '../../../services/types';
import { RouteNames } from '../../config';
import AddModal from '../featuredCard/Create/AddModal';
import { AbtestInfoBox } from './Displays';
import { MoreMenu } from './MoreMenu';
import useStyles from './styles';

interface IProps {
  type: 'featuredcard' | 'collection';
  status: ISegmentCurationStatus | 'all';
  onClickRowItem: (segmentItem: IUserSegment) => void;
  onError: (error: Error) => void;
  onDeleteCuration: (curationid: number) => Promise<AxiosResponse>;
  onUpdateCuration: (curationid: number, data: IAbTestUpdateRequestDto) => Promise<AxiosResponse>;
}

export const AbtestScheduleCurationList = (props: IProps): JSX.Element => {
  const classes = useStyles();

  const confirm = useConfirmDialog();
  const backdrop = useBackdrop();
  const snackbar = useSnackbar();
  const path = RouteNames.UserSegment;

  // states
  const listCanceler = useRef<Canceler>(() => undefined);
  const variantCanceler = useRef<Canceler>(() => undefined);
  const [featuredcardItems, setFeaturedcardItems] = useState<ISegmentCurationAbTest[]>([]);
  const [collectionItems, setCollectionItems] = useState<ISegmentCurationAbTest[]>([]);
  const [page, setPage] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(4);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [showFeaturedCardModal, setShowFeaturedCardModal] = useState<boolean>(false);
  const [storyAnchorData, setStoryAnchorData] = useState<null | IStory[]>(null);
  const [storyAnchorEl, setStoryAnchorEl] = useState<null | HTMLElement>(null);
  const [variantsAnchorId, setVariantsAnchorId] = useState<null | number>(null);
  const [abtestToShow, setAbtestsToShow] = useState<null | ISegmentCurationAbTest>(null);

  // refs
  const menuFocusedRef = useRef<ISegmentCurationAbTest>();
  const featuredCardRef = useRef<ISegmentCurationAbTest>();

  // handlers
  const handleMoreMenuClick = (ele: HTMLElement, item: ISegmentCurationAbTest) => {
    menuFocusedRef.current = item;
    setAnchorEl(ele);
  };
  const handleMoreMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, [anchorEl]);
  const handleClickFeaturedCard = (item: ISegmentCurationAbTest) => {
    featuredCardRef.current = item;
    setShowFeaturedCardModal(true);
  };
  const handleStoryCountClick = (event: React.MouseEvent<HTMLButtonElement>, cl: ISegmentCurationCollection) => {
    event.stopPropagation();
    event.preventDefault();
    setStoryAnchorEl(event.currentTarget);
    fetchCollectionData(cl.object.trackingId!);
  };
  const handleStoryPopoverClose = () => {
    setStoryAnchorEl(null);
    setStoryAnchorData(null);
  };
  const onClickVariantsCount = async (ele: HTMLElement, abtestId: number) => {
    try {
      if (variantsAnchorId === abtestId) {
        setAbtestsToShow(null);
        setVariantsAnchorId(null);
      } else {
        const abtestItem = await fetchAbtest(abtestId);
        if (abtestItem) {
          setAbtestsToShow(abtestItem);
          setVariantsAnchorId(abtestId);
        }
      }
    } catch (e) {}
  };
  const onSaveFeaturedCard = () => {
    fetchListData(page, rowsPerPage);
    setShowFeaturedCardModal(false);
  };

  // API
  const fetchListData = async (fetchPage: number, fetchPerPage: number) => {
    backdrop.show();
    listCanceler.current();

    try {
      if (props.type === 'featuredcard') {
        const [fetchPromise, cancel] = getUserSegmentAbtests<ISegmentCurationAbTest>(
          props.status,
          ISegmentCurationType.FeaturedCards,
          fetchPage + 1,
          fetchPerPage,
        );
        listCanceler.current = cancel;

        const result = await fetchPromise;
        setFeaturedcardItems(result.data.items);
        setTotalCount(result.data.totalCount || 0);
      } else {
        const [fetchPromise, cancel] = getUserSegmentAbtests<ISegmentCurationAbTest>(
          props.status,
          ISegmentCurationType.FictionSerials,
          fetchPage + 1,
          fetchPerPage,
        );
        listCanceler.current = cancel;

        const result = await fetchPromise;
        setCollectionItems(result.data.items);
        setTotalCount(result.data.totalCount || 0);
      }
    } catch (e) {
      if (api.isCancel(e)) return;
    } finally {
      backdrop.hide();
    }
  };
  const handleOnActivate = async () => {
    requestAction('Status change', 'Are you sure you want to start this A/B test now?', () =>
      props.onUpdateCuration(menuFocusedRef.current!.id!, { startsAt: new Date(), endsAt: menuFocusedRef.current!.endsAt! }),
    );
  };
  const handleOnComplete = async () => {
    const now = moment();
    requestAction(
      'Status change',
      now.isBefore(menuFocusedRef.current!.startsAt!)
        ? `You cannot resume this test once it's completed. Are you sure you want to end the test?`
        : `You cannot resume this test once it's completed. Are you sure you want to complete the test?`,
      () =>
        props.onUpdateCuration(menuFocusedRef.current!.id!, {
          endsAt: now.toDate(),
          startsAt: now.isSameOrBefore(menuFocusedRef.current!.startsAt!) ? now.add(-10, 'seconds').toDate() : menuFocusedRef.current!.startsAt!,
        }),
    );
  };
  const handleOnDelete = async () => {
    requestAction('Delete', `Are you sure you want to delete this A/B Test?`, () => props.onDeleteCuration(menuFocusedRef.current!.id!));
  };
  const requestAction = (title: string, text: string, res: () => Promise<AxiosResponse>) => {
    confirm.open({
      title: title,
      text: text,
      onProceed: () => {
        res()
          .then(() => {
            fetchListData(page, rowsPerPage);
            setAnchorEl(null);
          })
          .catch(e => {
            console.log(e);
            snackbar.error(e);
          });
      },
    });
  };
  const fetchCollectionData = async (trackingId: string) => {
    try {
      const [fetchPromise, cancel] = getCollection(trackingId);
      const result = await fetchPromise;

      if (result && result.status === 200) {
        setStoryAnchorData(result.data.stories);
      }
    } catch (e) {
      if (api.isCancel(e)) return;
      console.error('e', e);
    } finally {
    }
  };
  const fetchAbtest = async (abtestId: number) => {
    variantCanceler.current();

    try {
      const [fetchPromise, cancel] = getUserSegmentAbtest(abtestId);
      variantCanceler.current = cancel;

      const result = await fetchPromise;
      return result.data;
    } catch (e) {
      props.onError(e.response.data.message);
      if (api.isCancel(e)) return;
    } finally {
    }
  };

  // watches
  useEffect(() => {
    fetchListData(page, rowsPerPage);
  }, [page, rowsPerPage]);
  useEffect(() => {
    fetchListData(0, rowsPerPage);
  }, [props.status]);

  return (
    <Paper>
      <Typography className={classes.listTitle}>{props.type === 'featuredcard' ? 'Featured Card' : 'Collections'}</Typography>
      {props.type === 'featuredcard' && (
        <Table component="table" className={classes.table}>
          <TableHead component="thead">
            <TableRow component="tr">
              <TableCell component="th" align="center" className={classes.cellStatus}>
                Status
              </TableCell>
              <TableCell component="th" align="center" className={classes.cellPriority}>
                Variant
              </TableCell>
              <TableCell component="th" align="center" className={classes.cellId}>
                A/B Test ID
              </TableCell>
              <TableCell component="th" align="center" className={classes.cellCover}>
                Cover
              </TableCell>
              <TableCell component="th">Test Target</TableCell>
              <TableCell component="th" align="center">
                Segment
              </TableCell>
              <TableCell component="th" align="center">
                User Count
              </TableCell>
              <TableCell component="th" align="center" className={classes.cellDateTimeDouble}>
                Test Period
              </TableCell>
              <TableCell component="th" className={classes.cellMore}></TableCell>
            </TableRow>
          </TableHead>
          {featuredcardItems.length > 0 &&
            featuredcardItems.map((item, idx) => {
              return (
                <TableBody component="tbody" key={`schedule-featured-item-${item.id}-${idx}`}>
                  <TableRow component="tr" className={variantsAnchorId === item.id ? classes.trWithAbTest : ''}>
                    <TableCell component="td" align="center">
                      <ActiveStatusLabel startsAt={item.startsAt!} endsAt={item.endsAt!} />{' '}
                    </TableCell>
                    <TableCell component="td" align="center">
                      <span className={classes.popoverLink} onClick={e => onClickVariantsCount(e.currentTarget, item.id!)}>
                        {item.variants?.length}
                      </span>
                    </TableCell>
                    <TableCell component="td" align="center">
                      <CopyToClipboard content={item.id!} />
                    </TableCell>
                    <TableCell component="td" align="center">
                      <div
                        style={{
                          backgroundImage: `url(${process.env.CDN_URL}/${
                            (item.homeCuration as ISegmentCurationFeaturedCard)!.object.displayCoverUrl
                          }?w=320)`,
                        }}
                        className={classes.coverImageSmall}
                      />
                    </TableCell>
                    <TableCell component="td">
                      <Link
                        to={`#`}
                        onClick={e => {
                          e.preventDefault();
                          handleClickFeaturedCard(item);
                        }}
                        className={`${classes.popoverLink} ${classes.nowrapDots}`}
                      >
                        {item.homeCuration!.object.title}
                      </Link>
                    </TableCell>
                    <TableCell component="td" align="center">
                      <Link
                        to={`${path}/v/${item.homeCuration!.segment!.id!}/curation`}
                        onClick={e => {
                          e.stopPropagation();
                          props.onClickRowItem(item.homeCuration!.segment!);
                        }}
                        className={`${classes.popoverLink} ${classes.nowrapDots}`}
                      >
                        {item.homeCuration!.segment!.name}
                      </Link>
                    </TableCell>
                    <TableCell component="td" align="center">
                      {item.homeCuration!.segment!.userCount!}
                    </TableCell>
                    <TableCell component="td" align="center">
                      {moment(item.startsAt).format('lll')} ~ {item.endsAt ? moment(item.endsAt).format('lll') : ''}
                    </TableCell>
                    <TableCell component="td" align="center" className={classes.cellMore}>
                      <IconButton
                        size="small"
                        style={{}}
                        onClick={e => {
                          handleMoreMenuClick(e.currentTarget, item);
                        }}
                      >
                        <MoreIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  {variantsAnchorId === item.id && abtestToShow !== null && (
                    <TableRow component="tr" className={classes.trOfAbTest}>
                      <TableCell component="td" colSpan={3}></TableCell>
                      <TableCell component="td" colSpan={8}>
                        <AbtestInfoBox abtestItem={abtestToShow} />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              );
            })}
        </Table>
      )}
      {props.type === 'collection' && (
        <Table component="table" className={classes.table}>
          <TableHead component="thead">
            <TableRow component="tr">
              <TableCell component="th" align="center" className={classes.cellStatus}>
                Status
              </TableCell>
              <TableCell component="th" align="center" className={classes.cellPriority}>
                Variant
              </TableCell>
              <TableCell component="th" align="center" className={classes.cellId}>
                A/B Test ID
              </TableCell>
              <TableCell component="th">Test Target</TableCell>
              <TableCell component="th" align="center">
                Segment
              </TableCell>
              <TableCell component="th" align="center">
                User Count
              </TableCell>
              <TableCell component="th" align="center" className={classes.cellDateTimeDouble}>
                Test Period
              </TableCell>
              <TableCell component="th" className={classes.cellMore}></TableCell>
            </TableRow>
          </TableHead>
          {collectionItems.length > 0 &&
            collectionItems.map((item: ISegmentCurationAbTest, idx: number) => {
              return (
                <TableBody component="tbody" key={`schedule-collection-item-${item.id}-${idx}`}>
                  <TableRow component="tr" className={variantsAnchorId === item.id ? classes.trWithAbTest : ''}>
                    <TableCell component="td" align="center">
                      <ActiveStatusLabel startsAt={item.startsAt!} endsAt={item.endsAt!} />{' '}
                    </TableCell>
                    <TableCell component="td" align="center">
                      <span className={classes.popoverLink} onClick={e => onClickVariantsCount(e.currentTarget, item.id!)}>
                        {item.variants?.length}
                      </span>
                    </TableCell>
                    <TableCell component="td" align="center">
                      <CopyToClipboard content={item.id!} />
                    </TableCell>
                    <TableCell component="td">
                      <Link
                        to={`${RouteNames.Collection}/v/${(item.homeCuration!.object as ICollection).trackingId}`}
                        onClick={e => {
                          e.stopPropagation();
                        }}
                        target="_blank"
                        className={`${classes.popoverLink} ${classes.nowrapDots} ${classes.curationScheduleListTitleCollection}`}
                      >
                        {(item.homeCuration!.object as ICollection).internalTitle && (
                          <span className={classes.internalTitleEmphasize}>{(item.homeCuration!.object as ICollection).internalTitle}</span>
                        )}
                        {item.homeCuration!.object.title}
                      </Link>
                    </TableCell>
                    <TableCell component="td" align="center">
                      <Link
                        to={`${path}/v/${item.homeCuration!.segment!.id!}/curation`}
                        onClick={e => {
                          e.stopPropagation();
                          props.onClickRowItem(item.homeCuration!.segment!);
                        }}
                        className={`${classes.popoverLink} ${classes.nowrapDots}`}
                      >
                        {item.homeCuration!.segment!.name}
                      </Link>
                    </TableCell>
                    <TableCell component="td" align="center">
                      {item.homeCuration!.segment!.userCount!}
                    </TableCell>
                    <TableCell component="td" align="center">
                      {moment(item.startsAt).format('lll')} ~ {item.endsAt ? moment(item.endsAt).format('lll') : ''}
                    </TableCell>
                    <TableCell component="td" align="center" className={classes.cellMore}>
                      <IconButton
                        size="small"
                        style={{}}
                        onClick={e => {
                          handleMoreMenuClick(e.currentTarget, item);
                        }}
                      >
                        <MoreIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  {variantsAnchorId === item.id && abtestToShow !== null && (
                    <TableRow component="tr" className={classes.trOfAbTest}>
                      <TableCell component="td" colSpan={3}></TableCell>
                      <TableCell component="td" colSpan={8}>
                        <AbtestInfoBox abtestItem={abtestToShow} />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              );
            })}
        </Table>
      )}

      {totalCount !== undefined && (
        <TablePagination
          rowsPerPageOptions={[4, 8, 16]}
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={(e: unknown, v: number) => {
            setPage(v);
          }}
          onChangeRowsPerPage={(event: React.ChangeEvent<HTMLInputElement>) => {
            const rpp = parseInt(event.target.value, 10);
            setRowsPerPage(rpp);
            setPage(0);
          }}
        />
      )}
      {anchorEl && (
        <MoreMenu
          anchorEl={anchorEl}
          item={menuFocusedRef.current!}
          onClose={handleMoreMenuClose}
          onActivate={handleOnActivate}
          onComplete={handleOnComplete}
          onDelete={handleOnDelete}
        />
      )}
      {featuredCardRef && showFeaturedCardModal && (
        <Modal
          open={true}
          onEscapeKeyDown={() => {
            setShowFeaturedCardModal(false);
          }}
        >
          <div>
            <AddModal
              onSave={onSaveFeaturedCard}
              onClose={() => {
                setShowFeaturedCardModal(false);
              }}
              mode={'edit'}
              featuredCard={featuredCardRef?.current!.homeCuration!.object as IFeaturedCard}
              featuredCardId={featuredCardRef?.current!.homeCuration!.object.id}
            />
          </div>
        </Modal>
      )}
      {storyAnchorEl && storyAnchorData && (
        <Popover
          id={`story-list-popover`}
          open={true}
          anchorEl={storyAnchorEl}
          onClose={handleStoryPopoverClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <Box maxWidth={340} maxHeight={357} overflow="auto">
            <StoryList hideSearch={true} readonly={true} stories={storyAnchorData || []} additionalInfo={true} />
          </Box>
        </Popover>
      )}
    </Paper>
  );
};
